import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { MapPin } from 'lucide-react';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[350px]">
        <div className="absolute inset-0">
          <img
            src="/images/about-page.jpg"
            alt="Tuscan landscape"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50" />
        </div>
        <div className="relative h-full flex flex-col justify-center max-w-7xl mx-auto px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-white mb-4">
            About Italian Insiders
          </h1>
          <p className="text-white/80 text-lg max-w-xl">
            One person's curated recommendations. No algorithms, no sponsored content.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-16 lg:py-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-serif text-stone-900 mb-8">
            The problem with planning a trip to Italy
          </h2>
          
          <div className="prose prose-lg prose-stone">
            <p className="text-stone-600 leading-relaxed mb-6">
              I've lived in Italy for over a decade. And every time friends or family visit, they ask the same questions: <em>"Where should we eat? What should we see? Is that tour worth it?"</em>
            </p>
            
            <p className="text-stone-600 leading-relaxed mb-6">
              I used to send them links. Lots of links. A TripAdvisor page here, a blog post there, maybe a saved Google Maps list. But I noticed they'd still come back with stories of mediocre meals at tourist-trap restaurants, or spending a whole day on something that wasn't worth it.
            </p>

            <p className="text-stone-600 leading-relaxed mb-6">
              The problem isn't lack of information—it's too much of it. TripAdvisor has thousands of listings. Viator sells tickets to everything. Travel blogs bury the good stuff under SEO keywords and affiliate links.
            </p>

            <p className="text-stone-600 leading-relaxed mb-6">
              <strong>Italian Insiders is my solution:</strong> curated guides that cut through the noise. Not everything—just the good stuff. The restaurants where I'd take my own family. The viewpoints I actually visit. The day trips that are genuinely worth your time.
            </p>

            <p className="text-stone-600 leading-relaxed mb-8">
              Think of it as getting recommendations from a friend who happens to live there. Because that's exactly what it is.
            </p>
          </div>

          <div className="bg-stone-100 rounded-2xl p-8 mt-12">
            <h3 className="text-xl font-serif text-stone-900 mb-4">What this isn't</h3>
            <ul className="space-y-3 text-stone-600">
              <li className="flex items-start gap-3">
                <span className="text-amber-700 font-bold">×</span>
                <span>A travel agency. I don't book anything for you.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-700 font-bold">×</span>
                <span>A comprehensive guide. I don't cover everything—just what's worth your time.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-700 font-bold">×</span>
                <span>Sponsored content. Nobody pays to be listed here.</span>
              </li>
            </ul>
          </div>

          <div className="bg-stone-900 rounded-2xl p-8 mt-8 text-white">
            <h3 className="text-xl font-serif mb-4">What this is</h3>
            <ul className="space-y-3 text-white/80">
              <li className="flex items-start gap-3">
                <span className="text-amber-400 font-bold">✓</span>
                <span>Honest, curated recommendations from someone who actually lives here.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-400 font-bold">✓</span>
                <span>Clear, actionable info. No fluff, no filler.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-400 font-bold">✓</span>
                <span>The same tips I'd give my own friends and family.</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-serif text-stone-900 mb-6">
            Ready to plan your trip?
          </h2>
          <p className="text-stone-600 mb-8">
            Pick a destination and start exploring. The free basics are... free.
          </p>
          <Link
            to="/destinations"
            className="inline-block px-8 py-4 bg-stone-900 text-white rounded-full font-medium hover:bg-stone-800 transition-colors"
          >
            View Destinations
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
